// The Company row names companies three ways: name, website domain and
// company LinkedIn. Any match counts, so 4 names, 3 domains and 3 LinkedIn
// URLs are 10 alternatives (Lev, 2026-10-30). The People request folds the
// old scalar name (one typed name or domain) into those lists at the wire,
// so a scalar never ANDs with the lists into zero results. Saved state keeps
// its shape (the MCP and Clu hand-offs pin it); the row shows a scalar as one
// more name.

import { toast } from 'sonner';
import { MAX_SEARCH_COMPANIES } from '@/constants/search-limits';
import { normalizeDomain, normalizeDomainList } from '@/lib/normalize-domain';
import { normalizeCompanyLinkedInUrls } from '@/utils/people-search.utils';
import type { SearchFilters } from '@/types/search-filters';

const DOMAIN_SHAPE = /^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/;

/** A typed value that is a website domain: no space and a real hostname.
 *  The same rule the People request used for its scalar name. */
export function asTypedDomain(value: string): string | null {
  const text = value.trim();
  if (!text || /\s/.test(text)) return null;
  const domain = normalizeDomain(text);
  return domain && DOMAIN_SHAPE.test(domain) ? domain : null;
}

const appendUnique = (list: readonly string[], value: string): string[] =>
  list.some((v) => v.toLowerCase() === value.toLowerCase())
    ? [...list]
    : [...list, value];

/**
 * People: with other current-company names, domains or LinkedIn URLs set,
 * `companyNameFilter` joins them (`currentCompanyNames`, or
 * `currentCompanyDomains` when it is a domain). Alone it stays as it is, so
 * the request keeps staging's leaf in staging's place, byte-identical (the
 * API compiles the same way: review 2 S3-1, API 7141e9c35). Junk (not a
 * string) is dropped.
 */
export function foldPeopleCompanyScalar(filters: SearchFilters): SearchFilters {
  const raw = filters.companyNameFilter as unknown;
  const scalar = typeof raw === 'string' ? raw.trim() : '';
  if (!scalar) {
    return typeof raw === 'string'
      ? filters
      : { ...filters, companyNameFilter: '' };
  }
  // Decided on what the request will send, not on raw entries: a junk
  // LinkedIn value or a blank name compiles to nothing, so it doesn't count
  // (review 3 S3-1 / R3-4, as the API decides on normalized conditions).
  const others =
    (filters.currentCompanyNames ?? []).some(
      (v) => typeof v === 'string' && v.trim()
    ) ||
    normalizeDomainList(filters.currentCompanyDomains ?? []).length > 0 ||
    normalizeCompanyLinkedInUrls(filters.currentCompanyLinkedInUrls ?? [])
      .length > 0;
  if (!others) return filters;
  const domain = asTypedDomain(scalar);
  return {
    ...filters,
    companyNameFilter: '',
    ...(domain
      ? {
          currentCompanyDomains: appendUnique(
            filters.currentCompanyDomains ?? [],
            domain
          ),
        }
      : {
          currentCompanyNames: appendUnique(
            filters.currentCompanyNames ?? [],
            scalar
          ),
        }),
  };
}

/** The server's refusal, said before the search: names, domains and
 *  LinkedIn together name at most MAX_SEARCH_COMPANIES companies. */
export const COMPANY_CAP_MESSAGE = `Search at most ${MAX_SEARCH_COMPANIES.toLocaleString()} companies at a time across names, websites and LinkedIn URLs.`;

/** True, after saying why, when the Company row would name more companies
 *  than the server searches at once. The add is refused whole. */
export function overCompanyCap(total: number): boolean {
  if (total <= MAX_SEARCH_COMPANIES) return false;
  toast.error(COMPANY_CAP_MESSAGE);
  return true;
}

/** How many of `incoming` are new to `existing`, compared without case. */
export function countNew(
  existing: readonly string[],
  incoming: readonly string[]
): number {
  const seen = new Set(existing.map((v) => v.trim().toLowerCase()));
  let added = 0;
  for (const raw of incoming) {
    const key = raw.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    added += 1;
  }
  return added;
}

/** A company LinkedIn URL as a pill: "linkedin.com/company/stripe". */
export function linkedInPillLabel(url: string): string {
  return url
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/+$/, '');
}

/** The Company row's summary: "4 names, 3 domains, 3 LinkedIn", after any
 *  target-account labels. Undefined when the row names nothing. */
export function companyRowSummary({
  names,
  domains,
  linkedin = 0,
  extras = [],
}: {
  names: number;
  domains: number;
  linkedin?: number;
  extras?: string[];
}): string | undefined {
  const parts = [
    ...extras,
    names > 0 && `${names} ${names === 1 ? 'name' : 'names'}`,
    domains > 0 && `${domains} ${domains === 1 ? 'domain' : 'domains'}`,
    linkedin > 0 && `${linkedin} LinkedIn`,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : undefined;
}
